import React from 'react';

import { ReactComponent as AedAmountIcon } from '../assets/icons/aed-currency-icon.svg';
import { ReactComponent as EnergicAmountIcon } from '../assets/icons/amount-icon.svg';
import { ReactComponent as EnergicUnitIcon } from '../assets/icons/unit-icon.svg';
import energicConnectorAvailable from '../assets/images/available.png';
import energicCreditCard from '../assets/images/credit-card.png';
import energicCurrentForm from '../assets/images/current-form.png';
import energicLogo from '../assets/images/ev-logo.png';
import { ReactComponent as VoltieAmountIcon } from '../assets/voltie/icons/amount-icon.svg';
import { ReactComponent as VoltieUnitIcon } from '../assets/voltie/icons/unit-icon.svg';
import voltieConnectorAvailable from '../assets/voltie/images/available.png';
import voltieCreditCard from '../assets/voltie/images/credit-card.png';
import voltieCurrentForm from '../assets/voltie/images/current-form.png';
import voltieLogo from '../assets/voltie/images/voltie-logo.png';
// eslint-disable-next-line import/no-cycle
import { useBranding } from '../context/BrandingContext';
import getFirstSubdomain from '../shared-functions/DomiansplitFunction';
import { getLocalStorageItem } from '../shared-functions/LocalStorage';
// import { BrandingData } from '../utils/api/branding';

import { branding } from './enum';


type BrandingType = 'energic' | 'voltie';

/**
 * Interface for brand configuration
 */
export interface BrandConfig {
  images: {
    logo: string;
    creditCard: string;
    currentForm: string;
    connectorAvailable: string;
  };
  icons: {
    amountIcon: any;
    unitIcon: any;
    LoginBanner?: (properties: any) => React.ReactNode;
  };
  meta: {
    title: string;
    description: string;
    favicon?: string;
    footerText?: string;
  };
  links: {
    playStore: string;
    appStore: string;
  };
}




/**
 * Remote image renderer for API-driven branding
 * @param properties - image properties
 * @param properties.src - source URL
 * @param properties.alt - alt text
 * @returns JSX image element
 */
function RemoteImage(properties: { src: string; alt?: string }): JSX.Element {
  const { src, alt = 'logo', ...restProperties } = properties;

  return React.createElement('img', {
    src,
    alt,
    style: { maxWidth: '100%', height: 'auto', display: 'block' },
    ...restProperties,
  });
}

// Define image and icon mappings for each brand
const brandMapping = {
  [branding.ENERGIC]: {
    images: {
      logo: energicLogo,
      creditCard: energicCreditCard,
      currentForm: energicCurrentForm,
      connectorAvailable: energicConnectorAvailable,
    },
    icons: {
      amountIcon: EnergicAmountIcon,
      unitIcon: EnergicUnitIcon,
    },
    meta: {
      title: 'ENERGIC - Ultimate companion for a seamless EV charging experience.',
      description:
        'Designed with the modern EV driver in mind, our app empowers you to find, book, and manage charging sessions effortlessly. Transforming the way you charge, making every journey stress-free and sustainable. Say goodbye to range anxiety and hello to smarter, greener travel!',
    },
    links: {
      playStore: 'https://play.google.com/store/apps/details?id=sa.energic.chargex',
      appStore: 'https://apps.apple.com/us/app/energic-chargex/id6738176528?platform=iphone',
    },
  },
  [branding.VOLTIE]: {
    images: {
      logo: voltieLogo,
      creditCard: voltieCreditCard,
      currentForm: voltieCurrentForm,
      connectorAvailable: voltieConnectorAvailable,
    },
    icons: {
      amountIcon: VoltieAmountIcon,
      unitIcon: VoltieUnitIcon,
    },
    meta: {
      title: 'Voltie Power Up - Ultimate companion for a seamless EV charging experience.',
      description:
        'Designed with the modern EV driver in mind, our app empowers you to find, book, and manage charging sessions effortlessly. Transforming the way you charge, making every journey stress-free and sustainable. Say goodbye to range anxiety and hello to smarter, greener travel!',
    },
    links: {
      playStore: 'https://play.google.com/store/apps/details?id=us.voltie.consumer',
      appStore: 'https://apps.apple.com/us/app/voltie-powerup/id6740828992?platform=iphone',
    },
  },
};

/**
 * Fallback brand configuration (default is ENERGIC)
 */
const defaultBrand: BrandConfig = brandMapping[branding.ENERGIC] as BrandConfig;

/**
 * Dynamic Branding Api (Runtime + Build-time Fallback)
 */
const dynamicBrandingKey = `dynamicBranding_${getFirstSubdomain() || 'default'}`;
const dynamicBranding = getLocalStorageItem(dynamicBrandingKey);
const hasValidApiBranding = dynamicBranding && dynamicBranding.clientName;

// Determine base branding type (energic or voltie) from runtime data or default
export const currentBranding: BrandingType = (dynamicBranding?.branding as BrandingType) || 'energic';

// Initialize currentBrand based on the determined branding type
let currentBrand = brandMapping[currentBranding] || defaultBrand;

if (hasValidApiBranding) {
  const activeBranding = dynamicBranding;
  const resolvedLogo = activeBranding.logoUrl || activeBranding.logo;

  currentBrand = {
    ...currentBrand,

    meta: {
      ...currentBrand.meta,
      title: activeBranding.clientName || currentBrand.meta.title,
      description: activeBranding.description || currentBrand.meta.description,
    },

    images: {
      ...currentBrand.images,
      logo: resolvedLogo || currentBrand.images.logo,
    },

    icons: {
      ...currentBrand.icons,
    },

    /**
     * Logo component using API image
     * @param properties - component properties
     * @param properties.className - CSS class name
     * @returns JSX element
     */
    LogoComponent(properties: { className?: string }): JSX.Element {
      return React.createElement(RemoteImage, {
        src: resolvedLogo,
        alt: activeBranding.clientName,
        ...properties,
      });
    },
  } as any;

  console.log(`✅ API branding active for web-charge (Runtime - ${currentBranding}): ${activeBranding.clientName}`);
}

export const brandData = hasValidApiBranding ? dynamicBranding : null;

/**
 * Hook to get the current reactive branding.
 * @returns {BrandConfig} The current reactive brand configuration.
 */
export const useAppBranding = (): BrandConfig => {
  const { brandData: reactiveBrandData } = useBranding();

  const baseKey = (process.env.REACT_APP_BRANDING as keyof typeof brandMapping) || branding.ENERGIC;
  let brand: BrandConfig = (brandMapping[baseKey] as BrandConfig) ?? defaultBrand;

  if (reactiveBrandData) {
    brand = {
      ...brand,
      images: {
        ...brand.images,
        logo: reactiveBrandData.logo || brand.images.logo,
      },
      icons: {
        ...brand.icons,
        /**
         * Renders the dynamic login banner.
         * @param {object} properties - The component properties.
         * @returns {ReactNode} The login banner element.
         */
        LoginBanner: (properties: any) =>
          React.createElement(RemoteImage, {
            src: reactiveBrandData.logo || brand.images.logo,
            alt: reactiveBrandData.clientName || brand.meta.title,
            ...properties,
          }),
      },
      meta: {
        ...brand.meta,
        title: reactiveBrandData.clientName || brand.meta.title,
        description: reactiveBrandData.clientName || brand.meta.description,
        favicon: reactiveBrandData.favicon || brand.meta.favicon,
        footerText: reactiveBrandData.footer || brand.meta.footerText,
      },
    };
  }

  return brand;
};

export const {
  images: brandImage,
  icons: brandIcons,
  meta: metaValues,
  links: brandLinks,
} = currentBrand;

/**
 * Hook to get the active currency code.
 * Falls back to 'SAR' if not defined in dynamic branding data.
 * @returns {string} The active currency code.
 */
export const useCurrency = (): string => {
  const { brandData: reactiveBrandData } = useBranding();
  return reactiveBrandData?.currencyCode || 'SAR';
};

/**
 * Hook to get the translated currency text.
 * If the currency is 'SAR', returns t('currency') (which h`andles 'SAR'/'ريال' translation).
 * For other currencies, returns the currency code directly.
 * @param t - The i18n translate function.
 * @returns {string} The formatted currency string.
 */
export const useCurrencyText = (t?: (key: string) => string): string => {
  const currency = useCurrency();
  if (currency === 'SAR') {
    return t ? t('currency') : 'SAR';
  }
  return currency;
};

/**
 * A dynamic component that renders either the amount SVG icon (for SAR currency)
 * or the text of the custom currency (for other currencies like USD).
 * @param properties - component properties
 * @returns JSX element
 */
export function AmountIcon({ className }: { className?: string }): JSX.Element {
  const currencyCode = useCurrency();
  
  if (currencyCode === 'SAR') {
    const brand = useAppBranding();
    const Icon = brand.icons.amountIcon;
    return React.createElement(Icon, { className });
  }
  
  if (currencyCode === 'AED') {
    return React.createElement(AedAmountIcon, { className });
  }
  
  return React.createElement('span', { 
    className,
    style: { 
      fontSize: '11px', 
      fontWeight: 'bold',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'uppercase'
    } 
  }, currencyCode);
}

AmountIcon.defaultProps = {
  className: '',
};



